import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './article.css';
import Amalgam from '../../assets/images/Amalgam-logo.png';
import LinkedIn from '../../assets/images/LinkedIn.png';
import GreenCanvas from '../../assets/images/GreenCanvas.png';
import Breadcrumb from '../../assets/components/BreadCrumb';
import { useMediaQuery } from 'react-responsive';
import Arrow from '../../assets/images/Arrow1.png';
import { useNavigate } from 'react-router-dom';


function CleanItSupply() {
    const { pathname } = useLocation();
    const sectionRef = useRef<HTMLElement>(null);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const navigate = useNavigate();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const sections = entry.target.getElementsByClassName('content-section');
                    Array.from(sections).forEach((section) => {
                        if (entry.isIntersecting) {
                            section.classList.add('animate');
                        } else {
                            section.classList.remove('animate');
                        }
                    });
                });
            },
            {
                threshold: 0.2
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div className='article-container' >
            <div>
                <Breadcrumb route="/case-studies" pageTitle="CleanItSupply" />
            </div>
            <section className='article-row-1' id='cleanit-background'>
                <p>Retail</p>
                <h2>CleanItSupply</h2>
                <div className="article-details fade-in-right">
                    <div className="article-detail-item">
                        <span>Location: Jeffersonville, Pennsylvania 🇺🇸</span>
                    </div>
                </div>
            </section>

            <section className='article-intro-section'>
                <h1>How We Streamlined CleanItSupply's Operations for Enhanced Efficiency</h1>
                <p>
                    CleanItSupply is an online retailer for wholesale supplies, offering an extensive inventory that includes janitorial supplies, restaurant supplies, and home and office cleaning supplies. They cater to both small individual orders and large bulk orders, positioning themselves as a B2C and B2B retail business. Our collaboration focused on optimizing their operations to improve user experience and efficiency across their platform.
                </p>
            </section>

            <section className='article-row-2' ref={sectionRef}>
                <div className='content-section'>
                    <h3>Challenge</h3>
                    <p>CleanItSupply struggled with operational inefficiencies and suboptimal user experience caused by their outdated ASP.net-based website's slow loading times, limited scalability, and poor SEO performance.</p>
                </div>
                <div className='content-section'>
                    <h3>Approach</h3>
                    <p>We're rebuilding their website using React.JS for the frontend and introducing a new Rest API service to decouple the backend. We'll also transition the search functionality from Nextopia to SearchSpring for better product management and user experience.</p>
                </div>
                <div className='content-section'>
                    <h3>Results</h3>
                    <p>The site will be faster, more user-friendly, and scalable. Its search functionality will be stronger than ever and it'll be much easier to maintain. Upon project completion, they'll experience a boost in engagement, conversions, and customer satisfaction, as well as simplified future updates.</p>
                </div>
            </section>
            <section className='article-section-6'>
                <h2>The Challenge</h2>
                <h3>CleanItSupply's website has been running on an outdated ASP.net framework with tightly coupled frontend and backend.</h3>
                <p>
                    This has translated into slow loading and limited scalability for the site. The website would take about 20 seconds to load.
                    They were facing several risks:
                </p>
                <ul>
                    <li>A lower conversion rate, due to the slow loading times and navigation issues</li>
                    <li>A reduced SEO performance, due to the site's speed and poor crawlability</li>
                    <li>Limited scalability caused by growth restrictions</li>
                    <li>Operational inefficiencies due to poor maintainability and performance issues</li>
                </ul>
                <p>
                    For these reasons, CleanItSupply desired a modern solution that would decouple the front- from the backend and enhance the website's overall performance.
                    Every one-second delay in page loading can decrease conversion rates by 7%.
                    CleanItSupply had no front-end developers in-house—that's why they sought external expertise to redesign their website using contemporary technology.
                    The objective was to significantly improve the website's performance, as well as its maintainability, and usability.
                </p>
            </section>

            <section className='article-section-3'>

                <h2>The Solution</h2>
                <h3>Our Approach:</h3>
                <p>
                    We proposed to rebuild the CleanItSupply website using the React.JS library for the frontend. This approach would allow them to separate frontend from backend, which were interlinked through ASP.net. We have also revamped the backend to incorporate a new Rest API service that would support this new, decoupled architecture.
                </p>

                <h3>Our Step-by-Step Process:</h3>
                <ul>
                    <li>Understanding our client's need for a modern, high-performance website.</li>
                    <li>Converting designs into static images, and then into a React.JS application.</li>
                    <li>Modifying the ASP.net backend to include a new Rest API service.</li>
                    <li>Transitioning the search functionality from Nextopia to SearchSpring.</li>
                    <li>Tuning up the search functionality so it performs optimally, and making the management of products, promotions, and reporting more efficient.</li>
                    <li>Ensuring that all components perform at the desired level.</li>
                    <li>Staying up-to-date throughout the project with bi-weekly calls, mockup updates, and the creation of working React prototypes.</li>
                </ul>

                <h3>The Results</h3>
                <ul>
                    <li>Transitioning to a single-page application with React.JS will drastically decrease loading times, making CleanItSupply's website significantly faster. It'll go a long way in improving user engagement and conversion rates.</li>
                    <li>Splitting the frontend from the backend will facilitate the implementation of updates, features, and enhancements moving forward.</li>
                    <li>The improved search functionality will provide more relevant search results and efficient promotional management, enhancing the visitor experience.</li>
                </ul>
            </section>

            <section className='article-section-4'>
                <h2>Conclusions</h2>

                <h3>Summary of Benefits</h3>
                <p>
                    CleanItSupply will benefit from a modern, high performance website that is faster, decoupled and has a stronger search functionality. Moving forward, the site will be much easier to maintain.
                </p>

                <h3>Future Plans</h3>
                <p>
                    CleanItSupply will keep on collaborating with us as we explore further enhancements and optimizations to keep their website up-to-date with the technological landscape.
                </p>

                <h3>Contact Information</h3>
                <p>
                    Are you ready to leverage cutting-edge technology for your organization's needs?
                    Contact us at <a href="mailto:hello@amalgam-inc.com">hello@amalgam-inc.com</a>.
                </p>
            </section>

            <div className='green-canvas-container'>
                <img src={GreenCanvas} alt="" className='green-canvas-bg' />
            </div>
            <section className='ourwork-row-14 blog-row-14'>

                <h2>Get in touch</h2>
                <p>Reach out to us to gather more information or <br className='blog-row-14-break' />  set our wheels in motion.</p>
                <div className='ourwork-arrow-box blog-arrow-box'>
                    <img src={Arrow} alt="arrow" className='ourwork-arrow' />
                    <button onClick={() => navigate("/contactus")}>Let's talk</button>
                </div>

            </section>

            {!isMobile ? (
                <section className='home-footer'>
                    <div className='home-footer-middle'>
                        <img src={Amalgam} alt="Amalgam" className='home-footer-amalgam' />
                        <Link to="/" className='home-footer-links'>Home</Link>
                        <Link to="/ourwork" className='home-footer-links'>Our Work</Link>
                        <Link to="/aboutus" className='home-footer-links'>About Us</Link>
                        <Link to="/case-studies" className='home-footer-links'>Case Studies</Link>
                        <Link to="/startups" className='home-footer-links'>Launchpad</Link>
                        <Link to="/careers" className='home-footer-links'>Careers</Link>
                        <Link to="/contactus" className='home-footer-links'>Contact Us</Link>
                        <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' />
                    </div>
                    <div className='home-footer-dividor'>
                        <div className='home-footer-policies'>
                            <p><Link to="/privacy-policy">Privacy Policy</Link></p>
                            <p><Link to="/terms-and-conditions">Terms and Conditions</Link></p>
                            <p><Link to="/cookie-policy">Cookie Policy</Link></p>
                        </div>
                    </div>
                </section>
            ) : (
                <section>
                    <div className="footer-minimal">
                        <div className="footer-content">
                            {/* <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
                <img src={Amalgam} alt="Amalgam" className="footer-logo" />
                <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' style={{ alignSelf: "center" }} />
              </div> */}
                            <div className="footer-top-row">
                                <Link to="/" className="footer-link">Home</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/ourwork" className="footer-link">Our Work</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/aboutus" className="footer-link">About Us</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/case-studies" className="footer-link">Case Studies</Link>
                            </div>
                            <div className="footer-bottom-row">
                                <Link to="/startups" className="footer-link">Launchpad</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/careers" className="footer-link">Careers</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/contactus" className="footer-link">Contact</Link>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        width: "80%",
                        height: "0px",
                        borderTop: "1px solid black",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingBottom: "20px",
                        margin: "0 auto"
                    }}>
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "white",
                        gap: isMobile ? "5px" : "20px",
                        paddingBottom: "20px",
                        width: "100%",
                        padding: "0 10px 20px 10px",
                        textAlign: "center"
                    }}>
                        <Link to="/privacy-policy" style={{ textAlign: "center", color: "black", textDecoration: "none" }}>Privacy Policy</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/terms-and-conditions" style={{ color: "black", textDecoration: "none" }}>Terms and Conditions</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/cookie-policy" style={{ fontSize: "14px", color: "black", textDecoration: "none" }}>Cookie Policy</Link>
                    </div>
                </section>
            )}
        </div>
    );
}


export default CleanItSupply;